"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.WatcherDropdown = void 0;
var _react = _interopRequireWildcard(require("react"));
var _useSelectedOption = require("../../utils/hooks/useSelectedOption");
var _SelectBoxWrapper = require("../../utils/components/SelectBoxWrapper");
var _useTranslations = require("../../utils/i18n/useTranslations");
var _common = require("./common");
var _ProjectMemberMultiSelectValueTag = require("./ProjectMemberMultiSelectValueTag");
var _featureFlags = require("../../utils/featureFlags");
var _alloyReactDropdown = require("@adsk/alloy-react-dropdown");
var _DropdownUtils = require("../../utils/components/DropdownUtils");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const WatcherDropdown = _ref => {
  let {
    shouldIncludeMemberFn,
    selectedWatcherIds = [],
    onClose,
    onChange,
    defaultIsOpen,
    invalid,
    clearable = true,
    viewOnly = false
  } = _ref;
  const {
    value: shouldUseNewDropdown
  } = (0, _featureFlags.useFeatureFlag)(_featureFlags.FeatureFlag.OutdatedComponentMigrationPhase1Batch3);
  const isOptionDisabled = !shouldIncludeMemberFn ? () => false : option => !shouldIncludeMemberFn(option);
  const users = (0, _common.useProjectUsersAsOptions)({
    shouldIncludeMemberFn: option => !isOptionDisabled(option) || selectedWatcherIds.includes(option.value)
  });
  // options should be memoized to avoid re-rendering the dropdown
  // the options are constant once defined and should not be changed in the context of a single app use
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = (0, _react.useMemo)(() => users, [shouldIncludeMemberFn]);
  const selectedOptions = (0, _useSelectedOption.useReferentialSelectedOptions)(selectedWatcherIds, options);
  const isShowingRequiredSingleTag = selectedOptions.length === 1 && !clearable;
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const handleChange = newSelectedOptions => {
    if (!!newSelectedOptions) {
      onChange(newSelectedOptions.map(option => option.value));
    } else {
      // if the "clear" button is pressed, we shouldn't allow the disabled options to be removed
      onChange(options.filter(isOptionDisabled).map(option => option.value));
    }
  };
  const handleRemove = (0, _react.useCallback)(valueToRemove => {
    onChange(selectedOptions.filter(option => option.value !== valueToRemove).map(option => option.value));
  }, [onChange, selectedOptions]);
  (0, _react.useEffect)(() => {
    selectedWatcherIds.forEach(id => {
      const watcher = options.find(option => option.value === id);
      if (!watcher) {
        handleRemove(id);
      }
    });
  }, [handleRemove, options, selectedWatcherIds]);
  const OldDropdown = () => /*#__PURE__*/_react.default.createElement(_SelectBoxWrapper.SelectBoxWrapper, {
    multiple: true,
    showValueAsTags: true,
    disabled: viewOnly,
    valueTagRenderer: option => /*#__PURE__*/_react.default.createElement(_ProjectMemberMultiSelectValueTag.ProjectMemberMultiSelectValueTag, _extends({
      key: option.value
    }, option, {
      isDisabled: isOptionDisabled(option) || isShowingRequiredSingleTag,
      disabledTooltipContent: isShowingRequiredSingleTag ? T.translate("validation.required") : T.translate("issueEditForm.watchers.cannotRemoveWatcherFromAnotherCompany"),
      onRemove: handleRemove
    })),
    placeholder: T.translate("issueCreation.watchers.placeholder"),
    value: selectedOptions,
    onChange: handleChange,
    options: options,
    isOptionDisabled: isOptionDisabled,
    clearable: clearable,
    searchable: true,
    onClose: onClose,
    defaultIsOpen: defaultIsOpen,
    invalid: invalid
  });
  const semanticIdentifier = "watcher-dropdown";
  const NewDropdown = () => {
    const {
      controlProps,
      menuProps
    } = (0, _alloyReactDropdown.useMultiAutocomplete)({
      items: options,
      selectedItems: selectedOptions,
      onSelectedItemsChange: _ref2 => {
        let {
          selectedItems
        } = _ref2;
        return handleChange(selectedItems);
      },
      defaultIsOpen,
      id: semanticIdentifier
    });
    return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_alloyReactDropdown.MultiAutocompleteTokenControl, _extends({}, controlProps, {
      "data-testid": "".concat(semanticIdentifier, "-control"),
      style: {
        maxHeight: 340
      },
      placeholder: T.translate("issueCreation.watchers.placeholder"),
      onBlur: onClose,
      disabled: viewOnly,
      renderItems: () => /*#__PURE__*/_react.default.createElement("div", {
        // needed to achieve styling parity with SelectBox
        style: {
          display: "flex",
          flexWrap: "wrap",
          overflowX: "hidden"
        }
      }, (selectedOptions || []).map(selectedItem => /*#__PURE__*/_react.default.createElement(_ProjectMemberMultiSelectValueTag.ProjectMemberMultiSelectValueTag, _extends({
        key: selectedItem.value
      }, selectedItem, {
        isDisabled: isOptionDisabled(selectedItem) || isShowingRequiredSingleTag,
        disabledTooltipContent: isShowingRequiredSingleTag ? T.translate("validation.required") : T.translate("issueEditForm.watchers.cannotRemoveWatcherFromAnotherCompany"),
        onRemove: handleRemove
      }))))
    })), /*#__PURE__*/_react.default.createElement(_alloyReactDropdown.MultiSelectMenu, _extends({}, menuProps, {
      renderMenuItem: _ref3 => {
        let {
          item,
          ...itemProps
        } = _ref3;
        return (
          /*#__PURE__*/
          // @ts-ignore
          _react.default.createElement(_alloyReactDropdown.MenuItem, _extends({}, itemProps, {
            disabled: isOptionDisabled(item),
            key: item.value
          }))
        );
      },
      "data-testid": "watcher-dropdown-menu",
      renderFooter: () => (0, _DropdownUtils.DropdownFooter)({
        isClearable: clearable,
        handleChange,
        shouldDisplay: (selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.length) > 0
      })
    }), menuProps.menuItems.length === 0 && controlProps.getInputProps() && /*#__PURE__*/_react.default.createElement(_alloyReactDropdown.NoAutocompleteResults, {
      style: {}
    })));
  };
  return shouldUseNewDropdown ? NewDropdown() : OldDropdown();
};
exports.WatcherDropdown = WatcherDropdown;
//# sourceMappingURL=WatcherDropdown.js.map